// Answers arrive as markdown, but what people do with them is paste into an
// Outlook reply to a customer. Copying the raw source put literal `**` around
// every bold phrase and a blank line between every bullet — the model writes
// loose lists, which read fine once rendered and badly as plain text.
//
// So the copy button writes two flavours: the rendered HTML (Outlook and Word
// take this one, keeping bold and bullets) and this cleaned-up plain text as
// the fallback for anything that only accepts text.

export function markdownToPlainText(md: string): string {
  return md
    // inline code and code fences: keep the contents, drop the ticks
    .replace(/```[a-z]*\n?/gi, '')
    .replace(/`([^`]+)`/g, '$1')
    // emphasis
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/__([^_]+)__/g, '$1')
    // headings keep their words, lose their hashes
    .replace(/^\s{0,3}#{1,6}\s+/gm, '')
    // a link is useless without its target once the markup is gone
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '$1 ($2)')
    // the actual complaint: blank lines between list items
    .replace(/\n{2,}(?=\s*(?:[-*+]|\d+\.)\s)/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

// Prefers a rich-text copy so a pasted answer looks like an answer. Falls back
// to plain text when the browser has no ClipboardItem (older Safari, and any
// non-secure context) rather than failing silently.
export async function copyAnswer(html: string | undefined, markdown: string): Promise<void> {
  const plain = markdownToPlainText(markdown);

  if (html && typeof ClipboardItem !== 'undefined' && navigator.clipboard?.write) {
    try {
      await navigator.clipboard.write([
        new ClipboardItem({
          'text/html': new Blob([html], { type: 'text/html' }),
          'text/plain': new Blob([plain], { type: 'text/plain' }),
        }),
      ]);
      return;
    } catch {
      // fall through — a rejected rich copy should still leave something on
      // the clipboard rather than nothing
    }
  }
  await navigator.clipboard.writeText(plain);
}
